import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import PaymentModal from '../screen/settings/payments/PaymentModal';
import './PlanSelector.css';
import { sendRequest } from '../api/shootAPI';
import API from '../api/endpoints';
/* eslint-disable max-len */
/* eslint-disable no-nested-ternary */

const features = [
  'Desktop Time Tracking App',
  'Members',
  'Timesheets',
  'Screenshot Frequency',
  'Webcam Audit',
  'Team Engagement',
  'Geo Dashboard',
  'Insider Threats',
  'Settings & Permissions',
  'Time Off Requests',
  'Scheduled Email',
  'Two-factor Authentication',
  'Help Center',
  'Chat Support',
  'Email Support SLA (24x5)',
  'Data Retention',
];

function transformData(plans) {
  // TableHeader Transformation
  const TableHeader = plans.map((plan) => ({
    id: plan.id,
    plan: plan.name,
    description: plan.description,
    price: plan.price,
    recommended: plan.recommended,
    Members: plan.Members, // Add Members field for minimum logic
  }));

  // TableBody Transformation
  const TableBody = features.map((feature) => {
    const row = { feature };
    plans.forEach((plan) => {
      let value = plan[feature];

      // Format Data Retention to show "X days"
      if (feature === 'Data Retention' && typeof value === 'number') {
        value = `${value} days`;
      }

      // eslint-disable-next-line no-nested-ternary
      row[plan.name.toLowerCase()] = value === true ? 'Included'
        : value === false ? 'NA'
          : value;
    });
    return row;
  });

  return { TableHeader, TableBody };
}

function PlanSelector({
  activeWorkspace,
  formData,
  setFormData,
  currentStep,
  setCurrentStep,
  onPlanSelect,
  context,
  isRenewing = false,
  onSubscriptionSuccess,
}) {
  const [opened, setOpened] = useState(false);
  const [selectedMembership, setSelectedMembership] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [membersCount, setMembersCount] = useState(5);
  const [tableHeader, setTableHeader] = useState([]);
  const [tableBody, setTableBody] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMembershipId, setCurrentMembershipId] = useState(activeWorkspace?.membership || null);

  useEffect(() => {
    setCurrentMembershipId(activeWorkspace?.membership || null);
  }, [activeWorkspace?.membership]);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      let count = 5;
      if (activeWorkspace) {
        const res = await sendRequest({
          method: 'get',
          url: API.WORKSPACE.membersCount(activeWorkspace.id),
        });
        count = Math.max(res.data?.membersCount || 1, 5);
      } else if (formData) {
        count = Math.max(5 + (formData.workspaceMembers?.length || 0), 5);
      }
      setMembersCount(count);
      const res = await sendRequest({
        method: 'get',
        url: API.WORKSPACE.memberships(),
      });
      if (res.success && res.data) {
        const { TableHeader, TableBody } = transformData(res.data);
        setTableHeader(TableHeader);
        setTableBody(TableBody);
      }
      setLoading(false);
    }
    fetchData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeModal = () => {
    setOpened(false);
  };

  const openModal = (membership, plan) => {
    // For pricing page context, call onPlanSelect directly instead of opening modal
    if (context === 'pricing') {
      if (onPlanSelect) {
        onPlanSelect(plan);
      }
      return;
    }

    setSelectedMembership(membership);
    setSelectedPlan(plan);
    setOpened(true);
  };

  // Resolve the button label / disabled state for a single plan card.
  // In renewal mode (?renew=true) the workspace's current plan offers an enabled
  // "Renew" action regardless of whether the subscription is still active or has
  // already expired — renewing is exactly what an expired subscriber needs to do.
  const getPlanButtonProps = (plan) => {
    const isCurrentPlan = context === 'settings' && currentMembershipId === plan.id;
    const isDisabled = isCurrentPlan && !isRenewing;
    let label;
    if (isCurrentPlan) {
      label = isRenewing ? 'Renew' : 'Current Plan';
    } else {
      label = `Choose ${plan.plan}`;
    }
    return { isDisabled, label };
  };

  // Context-specific rendering
  return (
    <div className={`plan-selector-container ${context === 'signup' ? 'signup-context' : ''} ${context === 'settings' ? 'settings-context' : ''}`}>
      <div className="plan-selector-header">
        <h1 className="plan-selector-title">
          Choose Your
          {' '}
          <span className="highlight">Perfect</span>
          {' '}
          Workflow
        </h1>
        <p className="plan-selector-subtitle">
          Streamline workflows and enhance productivity with our comprehensive time tracking solution
        </p>
        <div className="plan-selector-info-badge">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10 0C8.02219 0 6.08879 0.586491 4.4443 1.6853C2.79981 2.78412 1.51809 4.3459 0.761209 6.17317C0.00433286 8.00043 -0.193701 10.0111 0.192152 11.9509C0.578004 13.8907 1.53041 15.6725 2.92894 17.0711C4.32746 18.4696 6.10929 19.422 8.0491 19.8079C9.98891 20.1937 11.9996 19.9957 13.8268 19.2388C15.6541 18.4819 17.2159 17.2002 18.3147 15.5557C19.4135 13.9112 20 11.9778 20 10C20 8.68678 19.7413 7.38642 19.2388 6.17317C18.7363 4.95991 17.9997 3.85752 17.0711 2.92893C16.1425 2.00035 15.0401 1.26375 13.8268 0.761205C12.6136 0.258658 11.3132 0 10 0ZM10 18C8.41775 18 6.87104 17.5308 5.55544 16.6518C4.23985 15.7727 3.21447 14.5233 2.60897 13.0615C2.00347 11.5997 1.84504 9.99113 2.15372 8.43928C2.4624 6.88743 3.22433 5.46197 4.34315 4.34315C5.46197 3.22433 6.88743 2.4624 8.43928 2.15372C9.99113 1.84504 11.5997 2.00346 13.0615 2.60896C14.5233 3.21447 15.7727 4.23984 16.6518 5.55544C17.5308 6.87103 18 8.41775 18 10C18 12.1217 17.1572 14.1566 15.6569 15.6569C14.1566 17.1571 12.1217 18 10 18Z"
              fill="#293855"
            />
            <path
              d="M10 5C9.73478 5 9.48043 5.10536 9.29289 5.29289C9.10536 5.48043 9 5.73478 9 6V7C9 7.26522 9.10536 7.51957 9.29289 7.70711C9.48043 7.89464 9.73478 8 10 8C10.2652 8 10.5196 7.89464 10.7071 7.70711C10.8946 7.51957 11 7.26522 11 7V6C11 5.73478 10.8946 5.48043 10.7071 5.29289C10.5196 5.10536 10.2652 5 10 5ZM10 9C9.73478 9 9.48043 9.10536 9.29289 9.29289C9.10536 9.48043 9 9.73478 9 10V14C9 14.2652 9.10536 14.5196 9.29289 14.7071C9.48043 14.8946 9.73478 15 10 15C10.2652 15 10.5196 14.8946 10.7071 14.7071C10.8946 14.5196 11 14.2652 11 14V10C11 9.73478 10.8946 9.48043 10.7071 9.29289C10.5196 9.10536 10.2652 9 10 9Z"
              fill="#293855"
            />
          </svg>
          Billed for a Minimum of
          {' '}
          {membersCount}
          {' '}
          User
          {membersCount !== 1 ? 's' : ''}
        </div>
      </div>

      {loading ? (
        <div className="plan-selector-loading">
          <p>Loading plans...</p>
        </div>
      ) : (
        <>
          {/* Plan Cards */}
          {tableHeader.length ? (
            <div className="plan-cards-container">
              {tableHeader.map((plan, i) => (
                <div
                  key={i}
                  className={`plan-card ${plan.recommended ? 'recommended' : ''}`}
                >
                  <div className="plan-card-content">
                    <div className="plan-card-header">
                      <h3 className="plan-card-name">{plan.plan}</h3>
                      {plan.recommended && (
                        <span className="plan-card-badge">
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="currentColor"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path d="M8 0L9.79611 5.52786H15.6085L10.9062 8.94427L12.7023 14.4721L8 11.0557L3.29772 14.4721L5.09383 8.94427L0.391548 5.52786H6.20389L8 0Z" />
                          </svg>
                          Recommended
                        </span>
                      )}
                    </div>
                    <p className="plan-card-description">{plan.description}</p>
                  </div>
                  <div className="plan-card-pricing">
                    <p className="plan-card-starts-at">Starts at</p>
                    <div className="plan-card-price-container">
                      <span className="plan-card-price">
                        $
                        {Number(plan.price).toFixed(2)}
                      </span>
                      <span className="plan-card-price-period">/month per user</span>
                    </div>
                  </div>
                  {(() => {
                    const { isDisabled, label } = getPlanButtonProps(plan);
                    return (
                      <button
                        type="button"
                        className={`plan-card-button ${plan.plan.toLowerCase()} ${isDisabled ? 'current-plan' : ''}`}
                        disabled={isDisabled}
                        onClick={() => openModal(plan.id, plan)}
                      >
                        {label}
                      </button>
                    );
                  })()}
                </div>
              ))}
            </div>
          ) : (
            <div className="plan-selector-loading">
              <p>No plans available at the moment.</p>
            </div>
          )}

          {/* Features Comparison Table */}
          {tableBody.length > 0 && (
            <div className="features-table-container">
              <table className="features-comparison-table">
                <thead>
                  <tr>
                    <th>Features</th>
                    {tableHeader.map((plan, i) => (
                      <th key={i}>{plan.plan}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableBody.map((row, i) => (
                    <tr key={i}>
                      <td>{row.feature}</td>
                      <td>
                        {row.basic === 'Included' ? (
                          <span className="feature-checkmark">✓</span>
                        ) : row.basic === 'NA' ? (
                          <span className="feature-na">-</span>
                        ) : (
                          row.basic
                        )}
                      </td>
                      <td>
                        {row.team === 'Included' ? (
                          <span className="feature-checkmark">✓</span>
                        ) : row.team === 'NA' ? (
                          <span className="feature-na">-</span>
                        ) : (
                          row.team
                        )}
                      </td>
                      <td>
                        {row.professional === 'Included' ? (
                          <span className="feature-checkmark">✓</span>
                        ) : row.professional === 'NA' ? (
                          <span className="feature-na">-</span>
                        ) : (
                          row.professional
                        )}
                      </td>
                      {/* <td> */}
                      {/*  {row.enterprise === 'Included' ? ( */}
                      {/*    <span className="feature-checkmark">✓</span> */}
                      {/*  ) : row.enterprise === 'NA' ? ( */}
                      {/*    <span className="feature-na">-</span> */}
                      {/*  ) : ( */}
                      {/*    row.enterprise */}
                      {/*  )} */}
                      {/* </td> */}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="features-table-footer">
                    {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                    <td>
                      {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                      <button
                        type="button"
                        className="feature-table-button"
                      />
                    </td>
                    {tableHeader.map((plan, i) => {
                      const { isDisabled, label } = getPlanButtonProps(plan);
                      return (
                        <td key={i}>
                          <button
                            type="button"
                            className={`feature-table-button ${plan.plan.toLowerCase()} ${isDisabled ? 'current-plan' : ''}`}
                            disabled={isDisabled}
                            onClick={() => openModal(plan.id, plan)}
                          >
                            {label}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </>
      )}

      {/* Signup Context Actions */}
      {context === 'signup' && (
        <div className="plan-selector-actions">
          {/* <Button */}
          {/*  fullWidth */}
          {/*  variant="outlined" */}
          {/*  onClick={() => { */}
          {/*    // eslint-disable-next-line chai-friendly/no-unused-expressions */}
          {/*    setFormData && setFormData({ ...formData, selectedPlan: null }); */}
          {/*    // eslint-disable-next-line chai-friendly/no-unused-expressions */}
          {/*    setCurrentStep && setCurrentStep({ value: currentStep.value + 1, isReturning: false }); */}
          {/*  }} */}
          {/*  style={{ maxWidth: '200px' }} */}
          {/* > */}
          {/*  Skip for Now */}
          {/* </Button> */}
          {/* <button */}
          {/*  type="button" */}
          {/*  className="back-link" */}
          {/*  onClick={() => setCurrentStep && setCurrentStep({ value: currentStep.value - 1, isReturning: true })} */}
          {/* > */}
          {/*  Go back */}
          {/* </button> */}
        </div>
      )}

      {/* Payment Modal */}
      {opened && (
        <PaymentModal
          closeModal={closeModal}
          membership={selectedMembership}
          plan={selectedPlan}
          activeWorkspace={activeWorkspace}
          membersCount={membersCount}
          onPaymentSuccess={() => {
            if (context === 'signup') {
              // eslint-disable-next-line chai-friendly/no-unused-expressions
              setFormData && setFormData({ ...formData, selectedPlan });
              // eslint-disable-next-line chai-friendly/no-unused-expressions
              setCurrentStep && setCurrentStep({ value: currentStep.value + 1, isReturning: false });
            } else {
              if (selectedMembership) setCurrentMembershipId(selectedMembership);
              setOpened(false);
              if (onSubscriptionSuccess) onSubscriptionSuccess(selectedMembership);
            }
            if (onPlanSelect) onPlanSelect(selectedPlan);
          }}
        />
      )}
    </div>
  );
}

PlanSelector.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  activeWorkspace: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  formData: PropTypes.object,
  setFormData: PropTypes.func,
  currentStep: PropTypes.shape({
    value: PropTypes.number,
    isReturning: PropTypes.bool,
  }),
  setCurrentStep: PropTypes.func,
  onPlanSelect: PropTypes.func,
  context: PropTypes.oneOf(['settings', 'signup', 'pricing']).isRequired,
  isRenewing: PropTypes.bool,
  onSubscriptionSuccess: PropTypes.func,
};

export default PlanSelector;
